import { createRouter, createWebHistory } from 'vue-router'
import { getModuleFromPath } from '@/utils'
import NotFoundPage from '@/views/NotFoundPage.vue'
import ResetMe from '@/components/ResetMe.vue'
import PageViewer from '@/services/PageViewer'
import { canAccessModule, canAccessPage } from '@/auth/ToolkitsAccess'
import { getPageKeyFromRoute } from '@/auth/ToolkitHelpers'
import { useGeneralStore } from '@/store/pinia/generalStore'
import { getRefreshToken } from '@/auth/tokenService'
import { authRoutes } from '@/routes/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    authRoutes,
    {
      path: '/reset-me',
      name: 'reset-me',
      component: ResetMe,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundPage
    },
  ]
})

// Lazy module loaders — each module is fetched only when first navigated to
// `preloadRecovery` calls preventDefault on `vite:preloadError`, and Vite's preload helper
// only rethrows when the event was not prevented. A stale chunk therefore makes the import
// resolve to `undefined` instead of rejecting, so every loader must tolerate that.
const moduleLoaders = {
  stock: () => import('@/routes/stock').then(m => m?.stockRoutes),
  buy: () => import('@/routes/buy').then(m => m?.buyRoutes),
  store: () => import('@/routes/store').then(m => m?.storepageRoutes),
  strategy: () => import('@/routes/strategy').then(m => m?.strategyRoutes),
  pricing: () => import('@/routes/pricing').then(m => m?.pricingRoutes),
}
const loadedModules = new Set()
// One in-flight promise per module. Without it, two navigations racing into the same module
// both pass the `loadedModules` check (it is only written after the await) and both call
// addRoute, re-registering named routes mid-navigation.
const loadingModules = new Map()

export async function ensureModuleRoutes (moduleName) {
  if (loadedModules.has(moduleName)) {
    return false
  }
  const inFlight = loadingModules.get(moduleName)
  if (inFlight) {
    // Waiters must also report `true` so their navigation re-resolves once the routes exist.
    await inFlight
    return true
  }
  const loader = moduleLoaders[moduleName]
  if (!loader) {
    return false
  }
  const promise = loader().then((route) => {
    if (!route) {
      // A stale chunk after a deploy. `preloadRecovery` has already started a reload, which
      // is asynchronous, so stop here rather than register `undefined` and keep navigating
      // inside a page that is about to be discarded.
      console.error(`Route module "${moduleName}" resolved empty; waiting for reload`)
      return new Promise(() => {})
    }
    router.addRoute(route)
    loadedModules.add(moduleName)
  })
  loadingModules.set(moduleName, promise)
  try {
    await promise
  } finally {
    // Dropped either way, so a failed import (stale chunk after a deploy) can be retried.
    loadingModules.delete(moduleName)
  }
  return true
}

// Map path prefixes to module names for route loading
function getRouteModule (path) {
  if (path.startsWith('/storepage')) {
    return 'store'
  }
  if (path.startsWith('/tasks')) {
    return 'stock'
  } // /tasks is a stock child route
  return getModuleFromPath(path) // stock, buy, strategy, pricing
}

function handleStorepageRoute (query) {
  if (query.token) {
    localStorage.setItem('storepage_token', query.token)
  }
}

router.beforeEach(async (to) => {
  // Lazy-load the route module for the target path
  const routeModule = getRouteModule(to.path)
  if (routeModule) {
    const added = await ensureModuleRoutes(routeModule)
    if (added) {
      // Re-resolve now that new routes are registered
      return to.fullPath
    }
  }

  if (to.path.includes('buy/home')) {
    return { path: '/buy/shopping' }
  }
  let currentModule = getModuleFromPath(to.path)
  const isStorePage = currentModule === 'store'
  const accessStock = localStorage.getItem('user_access_stock')
  const accessBuy = localStorage.getItem('user_access_buy')

  if (currentModule) {
    localStorage.setItem('lastVisitedModule', currentModule)
  } else {
    currentModule = localStorage.getItem('lastVisitedModule') || 'stock'
  }
  // Set synchronously so downstream components can read currentModule during their
  // first render. The prior setTimeout/dynamic-import delayed this by ~100ms, which
  // let SettingsConfiguration.vue's `:to="{name: currentModule + '...'}"` bindings
  // resolve to `"null-..."` and throw router no-match errors.
  useGeneralStore().setCurrentModule(currentModule)

  // Log page visit
  const pageName = to.path.split('/').filter(Boolean).pop() || to.name || 'unknown'
  PageViewer.pageVisited(currentModule, pageName, to.fullPath, to.query)

  if (isStorePage) {
    handleStorepageRoute(to.query)
  }

  // if user opens application root url
  if (to.path === '/') {
    if (['read', 'write'].includes(accessBuy) && !['read', 'write'].includes(accessStock)) {
      return { path: 'buy/home' }
    } else {
      if (currentModule.includes('buy')) {
        return { path: `/${currentModule}/shopping` }
      }
      return { path: `/${currentModule}/home` }
    }
  }
  if (
    isStorePage ||
    ['/login', '/reset_password', '/forgot-password'].includes(to.path)
  ) {
    return true
  } else if(getRefreshToken()) {
    const pageKey = getPageKeyFromRoute(to.name || '')

    const hasModuleAccess = await canAccessModule(currentModule)
    const hasPageAccess = await canAccessPage(currentModule, pageKey)

    if (!hasModuleAccess || !hasPageAccess) {
      console.log('User does not have access to this module or page', currentModule, pageKey)
      localStorage.setItem('show_access_denied_dialog', 'true')

      try {
        const { useGeneralStore } = await import('@/store/pinia/generalStore')
        const generalStore = useGeneralStore()
        generalStore.setAccessDeniedDialogVisible(true)
      } catch (error) {
        console.warn('Failed to set access denied dialog visible in store:', error)
      }

      return false
    }
    return true
  } else {
    return {
      path: '/login',
      query: { redirect: to.fullPath }
    }
  }
})

export default router
